/* ============================================================
   recovr — GSAP motion layer (Shopify Horizon theme asset)
   File: assets/recovr-gsap-motion.js  ·  v3 (section-agnostic)

   Loads AFTER gsap.min.js + ScrollTrigger.min.js (all deferred),
   on index + product + page templates (see layout/theme.liquid).

   GOALS (v3):
   1. COMPLETE coverage — every section on home, product and the
      content pages gets a scroll reveal, by iterating the actual
      Shopify section wrappers instead of a hand-kept class list.
      No section can be silently missed.
   2. ZOOM — each section's lead media eases from a gentle scale
      into place as it scrolls in.
   3. PARALLAX — large hero/figure media drift subtly on scroll
      (transform-only, scrubbed) for depth.
   4. SMOOTH & CONSISTENT — reveals are created with gsap.from()
      at init (hidden from first paint, so nothing flashes), and
      ScrollTrigger is refreshed after fonts/images settle so
      triggers never fire at stale positions on a long page.

   The CSS/JS motion (recovr-motion.js + [data-recovr-reveal] /
   .recovr-floaty) stays as the no-JS / script-blocked fallback;
   GSAP takes over via the `gsap-on` class on <html>, added only
   after a successful init. Brand easing cubic-bezier(.16,1,.3,1)
   ≈ "expo.out". Reduced motion → instant final states, always.
   Intensity: tasteful / premium (gentle scale, subtle drift).
   ============================================================ */

(function () {
  'use strict';

  // Wait for gsap + ScrollTrigger even if they resolve a beat late
  // (all three scripts are deferred, but order/network can vary).
  var tries = 0;
  (function boot() {
    if (window.gsap && window.ScrollTrigger) return start();
    if (tries++ < 60) return void setTimeout(boot, 50); // up to ~3s
    // Libraries never arrived — leave the CSS/JS fallback running.
  })();

  function start() {
    if (document.documentElement.classList.contains('gsap-on')) return; // guard double-init
    gsap.registerPlugin(ScrollTrigger);
    // Tell CSS that GSAP owns motion now (stands the CSS reveals/float down).
    document.documentElement.classList.add('gsap-on');

    var EASE = 'expo.out'; // ≈ cubic-bezier(.16,1,.3,1)
    var DUR = 0.9;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var toArr = function (x) { return Array.prototype.slice.call(x); };

    /* ------------------------------------------------------------------
       Data-attribute driven fills (bars / rings / counters / steps).
       Shared by both the reduced-motion path and the animated path.
       ------------------------------------------------------------------ */
    function fillsInstant() {
      document.querySelectorAll('[data-recovr-ring]').forEach(function (el) { el.classList.add('in'); });
      document.querySelectorAll('[data-recovr-bar]').forEach(function (el) {
        el.style.width = (parseFloat(el.dataset.recovrBar) || 0) + '%';
      });
      document.querySelectorAll('[data-recovr-count]').forEach(function (el) {
        el.textContent = '' + (parseFloat(el.dataset.recovrCount) || 0);
      });
      document.querySelectorAll('[data-recovr-step]').forEach(function (el) { el.classList.add('on'); });
    }

    /* ------------------------------------------------------------------
       REDUCED MOTION — everything rests in its final state, instantly.
       ------------------------------------------------------------------ */
    if (reduce) {
      gsap.set(
        '[data-recovr-reveal], .recovr-load > *, .recovr-hero__media, .recovr-hero__visual, ' +
          '.recovr-page-hero__media, .recovr-buybox__left, .recovr-buybox__right',
        { clearProps: 'all', opacity: 1, y: 0, x: 0, scale: 1 }
      );
      document.querySelectorAll('[data-recovr-reveal]').forEach(function (el) { el.classList.add('in'); });
      fillsInstant();
      document.addEventListener('shopify:section:load', function () { /* static, nothing to do */ });
      return;
    }

    // Premium-feel defaults + smoothness config.
    gsap.config({ force3D: true });
    ScrollTrigger.config({ ignoreMobileResize: true });

    var ctxTweens = [];
    var bigEnough = function (el, min) { return el && el.offsetHeight >= (min || 120); };

    /* Word-split a headline so it can reveal word-by-word (no SplitText license). */
    function splitTitle(el) {
      if (!el || el.dataset.recovrSplit) return;
      el.dataset.recovrSplit = '1';
      toArr(el.childNodes).forEach(function (node) {
        if (node.nodeType !== Node.TEXT_NODE || !/\S/.test(node.textContent)) return;
        var frag = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach(function (part) {
          if (/\S/.test(part)) {
            var span = document.createElement('span');
            span.className = 'recovr-w';
            span.style.display = 'inline-block';
            span.style.willChange = 'transform, opacity';
            span.textContent = part;
            frag.appendChild(span);
          } else {
            frag.appendChild(document.createTextNode(part));
          }
        });
        el.replaceChild(frag, node);
      });
    }

    /* ==================================================================
       1. HERO ENTRANCE (above the fold → play immediately on load)
       Home hero (.recovr-hero) and content-page hero (.recovr-page-hero).
       ================================================================== */
    (function heroes() {
      // Home hero
      var hero = document.querySelector('.recovr-hero');
      if (hero) {
        splitTitle(hero.querySelector('.recovr-hero__title'));
        var loadWrap = hero.querySelector('.recovr-load');
        var tl = gsap.timeline({ defaults: { ease: EASE, duration: DUR } });
        if (loadWrap) {
          var words = hero.querySelectorAll('.recovr-hero__title .recovr-w');
          var copy = toArr(loadWrap.children).filter(function (c) { return !c.querySelector('.recovr-w'); });
          if (words.length) tl.from(words, { y: 24, opacity: 0, stagger: 0.05 }, 0.05);
          tl.from(copy, { y: 22, opacity: 0, stagger: 0.09 }, words.length ? '-=0.5' : 0.05);
        }
        var media = hero.querySelector('.recovr-hero__media');
        if (media) tl.fromTo(media, { y: 34, opacity: 0, scale: 1.04 }, { y: 0, opacity: 1, scale: 1, duration: 1.1 }, '-=0.7');
        // Ambient float on the hero visual (replaces the CSS floaty keyframe).
        hero.querySelectorAll('.recovr-hero__visual').forEach(function (el) {
          ctxTweens.push(gsap.to(el, { y: -9, duration: 3.2, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 1 }));
        });
      }

      // Content-page hero (Our Story / Contact / FAQ)
      var pageHero = document.querySelector('.recovr-page-hero');
      if (pageHero) {
        splitTitle(pageHero.querySelector('h1'));
        var bits = pageHero.querySelectorAll('.recovr-eyebrow, h1 .recovr-w, h1, .recovr-page-hero__sub');
        var phMedia = pageHero.querySelector('.recovr-page-hero__media');
        var ptl = gsap.timeline({ defaults: { ease: EASE, duration: DUR } });
        if (bits.length) ptl.from(bits, { y: 20, opacity: 0, stagger: 0.09 }, 0.05);
        if (phMedia) ptl.fromTo(phMedia, { y: 30, opacity: 0, scale: 1.04 }, { y: 0, opacity: 1, scale: 1, duration: 1 }, '-=0.6');
      }
    })();

    /* ==================================================================
       2. BUY BOX — orchestrated entrance, now SCROLL-TRIGGERED so it
       works whether the buy box is section #1 (product page → fires on
       load, it's in view) or mid-page (home page → fires when reached).
       ================================================================== */
    (function buybox() {
      var box = document.querySelector('.recovr-buybox');
      if (!box) return;
      splitTitle(box.querySelector('.recovr-buybox__title'));
      // Faster, tighter cascade: shorter tweens + heavy overlap + smaller
      // travel, so the price, plan and Add-to-Cart land in ~1.5s instead of
      // trickling in over ~4s. `LAP` = each step starts 0.07s after the
      // previous one STARTS (not finishes), which reads as smooth + snappy.
      var BUY_DUR = 0.5;
      var LAP = '<0.07';
      var tl = gsap.timeline({
        defaults: { ease: EASE, duration: BUY_DUR },
        scrollTrigger: { trigger: box, start: 'top 82%', once: true },
      });
      var first = true;
      var step = function (sel, vars, pos) {
        if (!box.querySelector(sel)) return;
        tl.from(sel, vars, first ? 0 : (pos || LAP));
        first = false;
      };
      // TRUE visual top-to-bottom order, compressed.
      step('.recovr-buybox__stage', { y: 26, opacity: 0, scale: 1.02, duration: 0.65 });
      step('.recovr-buybox__thumb', { scale: 0.9, opacity: 0, stagger: 0.02, duration: 0.4 }, '<0.18');
      step('.recovr-buybox__toprow', { y: 12, opacity: 0 });
      step('.recovr-buybox__title .recovr-w', { y: 18, opacity: 0, stagger: 0.03 });
      step('.recovr-buybox__sub', { y: 12, opacity: 0 });
      step('.recovr-buybox__bullets li', { x: -10, opacity: 0, stagger: 0.045 });
      step('.recovr-badges__excl', { y: 10, opacity: 0, stagger: 0.035, duration: 0.42 });
      step('.recovr-buybox__price', { y: 10, opacity: 0 });
      step('.recovr-promo--banner', { y: 10, opacity: 0, scale: 0.99 });
      step('.recovr-plan-selector__label', { y: 8, opacity: 0 });
      step('.recovr-plan-selector__opt', { y: 14, opacity: 0, stagger: 0.05 });
      step('.recovr-scarcity', { y: 10, opacity: 0 });
      step('.recovr-buybox__form-buttons', { y: 12, opacity: 0 });
      step('.recovr-buybox__ship-note', { y: 8, opacity: 0 });
      step('.recovr-buybox__guarantee', { y: 10, opacity: 0 });
      // Ambient float on the gallery.
      var stage = box.querySelector('.recovr-buybox__stage');
      if (stage) ctxTweens.push(gsap.to(stage, { y: -8, duration: 3.2, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 1 }));
    })();

    /* ==================================================================
       3. CARD-GRID STAGGERS — repeated items rise in with a tight
       stagger per grid, so a page doesn't read as "everything the same".
       Records the grid PARENTS so the section-reveal pass (below) won't
       double-animate the same containers.
       ================================================================== */
    var staggeredParents = new WeakSet();
    function staggerGrid(sel, opts) {
      opts = opts || {};
      var items = gsap.utils.toArray(sel).filter(function (el) { return !el.dataset.recovrDone; });
      if (!items.length) return;
      var groups = new Map();
      items.forEach(function (el) {
        el.dataset.recovrDone = '1';
        var key = el.parentElement;
        staggeredParents.add(key);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(el);
      });
      groups.forEach(function (groupItems, parent) {
        gsap.from(groupItems, {
          y: opts.y != null ? opts.y : 26,
          opacity: 0,
          scale: opts.scale != null ? opts.scale : 1,
          duration: opts.duration != null ? opts.duration : 0.7,
          ease: EASE,
          stagger: opts.stagger != null ? opts.stagger : 0.08,
          scrollTrigger: { trigger: parent, start: 'top 85%', once: true },
        });
      });
    }
    staggerGrid('.recovr-values__card');
    staggerGrid('.recovr-reasons__card', { stagger: 0.06 });
    staggerGrid('.recovr-subscribe-benefits__item');
    staggerGrid('.recovr-support-cards__card');
    staggerGrid('.recovr-why__card', { stagger: 0.07 });
    staggerGrid('.recovr-leave-out__item', { y: 16, stagger: 0.05 });
    staggerGrid('.recovr-trust-bar__point', { y: 12, stagger: 0.05, duration: 0.5 });
    staggerGrid('.recovr-loved__tile', { stagger: 0.1 });
    staggerGrid('.recovr-product-cards__item', { stagger: 0.1 });
    staggerGrid('.cmp-row', { y: 18, stagger: 0.05, duration: 0.55 });
    staggerGrid('.recovr-stats__stat', { scale: 0.92, stagger: 0.1 });
    staggerGrid('.recovr-review-rail .rv', { stagger: 0.08 });
    staggerGrid('.recovr-video-testimonials__vid', { stagger: 0.08 });
    staggerGrid('.recovr-community__row', { y: 16, stagger: 0.08 });

    /* ==================================================================
       4. SECTION REVEALS (COMPLETE COVERAGE) — iterate the real Shopify
       section wrappers so EVERY content section is covered. For each,
       reveal its [data-recovr-reveal] blocks (or, if a section has none,
       its header + direct blocks) with a rise+fade stagger. Created with
       gsap.from() at init → hidden from first paint → no flash.
       Hero + buy box are already handled above and are skipped here.
       ================================================================== */
    var SKIP_ROOT = /recovr-hero\b|recovr-page-hero\b|recovr-buybox\b/;
    function isContentSection(rootEl) {
      if (!rootEl) return false;
      var cls = rootEl.className || '';
      if (SKIP_ROOT.test(cls)) return false;
      return /recovr-/.test(cls) || rootEl.querySelector('[data-recovr-reveal]');
    }
    function revealTargets(rootEl) {
      var marked = toArr(rootEl.querySelectorAll('[data-recovr-reveal]'));
      var list = marked.length ? marked : toArr((rootEl.querySelector('.recovr-wrap') || rootEl).children);
      // Drop containers whose children are already staggered as cards,
      // and drop anything nested inside another target (avoid double play).
      return list.filter(function (el) {
        if (el.dataset.recovrDone) return false;
        if (staggeredParents.has(el)) return false;
        if (el.querySelector && el.querySelector('[data-recovr-done="1"]')) {
          // container that only wraps staggered cards → skip; its cards animate.
          var kids = toArr(el.children);
          if (kids.length && kids.every(function (k) { return k.dataset.recovrDone || staggeredParents.has(k); })) return false;
        }
        return el.offsetParent !== null || el.getClientRects().length; // visible in layout
      });
    }

    var sectionWraps = document.querySelectorAll('.shopify-section, [id^="shopify-section-"]');
    var seenRoots = new WeakSet();
    (sectionWraps.length ? toArr(sectionWraps) : []).forEach(function (wrap) {
      var rootEl = wrap.querySelector(':scope > *') || wrap;
      if (seenRoots.has(rootEl) || !isContentSection(rootEl)) return;
      seenRoots.add(rootEl);
      var targets = revealTargets(rootEl);
      if (!targets.length) return;
      targets.forEach(function (el) { el.dataset.recovrDone = '1'; });
      gsap.from(targets, {
        y: 26, opacity: 0, duration: 0.85, ease: EASE, stagger: 0.08,
        scrollTrigger: { trigger: rootEl, start: 'top 84%', once: true },
      });
    });

    /* ==================================================================
       5. ZOOM + PARALLAX ON MEDIA (every section that has a figure)
       - Zoom: lead media eases from a gentle scale into place on enter.
       - Parallax: large hero/figure media drift subtly on scroll (scrub).
       Both are transform-only. Values kept tasteful so nothing overlaps
       neighbouring content or reveals frame edges.
       ================================================================== */
    var FIGURE_SEL = [
      '.recovr-hero__visual', '.recovr-page-hero__media', '.recovr-why__scene',
      '.recovr-formula-banner__media', '.recovr-ingredients__hero', '.recovr-story-split__media',
      '.recovr-how-it-works__card',
    ].join(', ');

    // Parallax (subtle drift). Skip the hero visual (it already floats).
    gsap.utils.toArray(FIGURE_SEL).forEach(function (el) {
      if (!bigEnough(el, 150)) return;
      if (el.matches('.recovr-hero__visual')) return; // floats already
      gsap.fromTo(el,
        { yPercent: -4 },
        { yPercent: 4, ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.6 } });
    });

    // Zoom on each section's lead image (covers sections without a named
    // figure too). One image per section, sizeable, not inside a card grid.
    (function leadZooms() {
      var done = new WeakSet();
      toArr(document.querySelectorAll('.shopify-section, [id^="shopify-section-"]')).forEach(function (wrap) {
        var rootEl = wrap.querySelector(':scope > *') || wrap;
        if (!isContentSection(rootEl) && !/recovr-hero|recovr-page-hero/.test(rootEl.className || '')) return;
        var fig = rootEl.querySelector(FIGURE_SEL);
        var img = (fig && fig.querySelector('img')) || fig ||
          toArr(rootEl.querySelectorAll('img')).filter(function (im) {
            return bigEnough(im, 170) && !im.closest('.recovr-buybox__thumbs');
          })[0];
        if (!img || done.has(img)) return;
        done.add(img);
        // Don't fight the buy-box stage / hero media entrances.
        if (img.closest('.recovr-buybox') || img.closest('.recovr-hero') || img.closest('.recovr-page-hero')) return;
        gsap.fromTo(img,
          { scale: 1.06 },
          { scale: 1, ease: EASE, duration: 1.1,
            scrollTrigger: { trigger: img, start: 'top 88%', once: true } });
      });
    })();

    /* ==================================================================
       6. SIGNATURE SECTION EFFECTS (unchanged behaviours, preserved)
       ================================================================== */
    // Progress rings — flip .in so the CSS var-driven fill animates.
    document.querySelectorAll('[data-recovr-ring]').forEach(function (el) {
      ScrollTrigger.create({ trigger: el, start: 'top 85%', once: true, onEnter: function () { el.classList.add('in'); } });
    });
    // Stat / community bars — animate width to target %.
    document.querySelectorAll('[data-recovr-bar]').forEach(function (el) {
      ScrollTrigger.create({
        trigger: el, start: 'top 88%', once: true,
        onEnter: function () { gsap.to(el, { width: (parseFloat(el.dataset.recovrBar) || 0) + '%', duration: 1.4, ease: EASE }); },
      });
    });
    // Count-up numbers.
    document.querySelectorAll('[data-recovr-count]').forEach(function (el) {
      var to = parseFloat(el.dataset.recovrCount) || 0;
      ScrollTrigger.create({
        trigger: el, start: 'top 85%', once: true,
        onEnter: function () { gsap.fromTo(el, { innerText: 0 }, { innerText: to, duration: 1.4, ease: 'power3.out', snap: { innerText: 1 } }); },
      });
    });
    // Scroll-expand timeline steps (how-it-works).
    document.querySelectorAll('[data-recovr-step]').forEach(function (el) {
      ScrollTrigger.create({ trigger: el, start: 'top 60%', once: true, onEnter: function () { el.classList.add('on'); } });
    });
    // Transformation timeline: dashed spine "draws" + dots pop.
    var tf = document.querySelector('.recovr-transformation .tf');
    if (tf) {
      gsap.fromTo(tf, { '--spine': '0%' }, { '--spine': '100%', ease: 'none',
        scrollTrigger: { trigger: tf, start: 'top 70%', end: 'bottom 60%', scrub: 0.5 } });
      gsap.utils.toArray('.recovr-transformation .tf-dot').forEach(function (dot) {
        gsap.from(dot, { scale: 0, ease: 'back.out(2.5)', duration: 0.5,
          scrollTrigger: { trigger: dot, start: 'top 78%', once: true } });
      });
    }

    /* ==================================================================
       7. HOVER MICRO-INTERACTIONS (pointer-fine devices only)
       ================================================================== */
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.querySelectorAll([
        '.recovr-video-testimonials__vid', '.recovr-how-it-works__card', '.recovr-survey__finding',
        '.recovr-transformation .tf-card', '.recovr-how-it-works__steps .step', '.recovr-values__card',
        '.recovr-reasons__card', '.recovr-subscribe-benefits__item', '.recovr-support-cards__card',
        '.recovr-why__card', '.recovr-loved__tile', '.recovr-product-cards__item', '.recovr-review-rail .rv',
      ].join(', ')).forEach(function (card) {
        var lift = gsap.quickTo(card, 'y', { duration: 0.4, ease: EASE });
        var scl = gsap.quickTo(card, 'scale', { duration: 0.4, ease: EASE });
        card.addEventListener('mouseenter', function () { lift(-5); scl(1.015); });
        card.addEventListener('mouseleave', function () { lift(0); scl(1); });
      });
      // Primary CTAs: magnetic pull toward the cursor.
      document.querySelectorAll('.recovr-buybox__atcbtn, .recovr-btn').forEach(function (btn) {
        var xTo = gsap.quickTo(btn, 'x', { duration: 0.5, ease: EASE });
        var yTo = gsap.quickTo(btn, 'y', { duration: 0.5, ease: EASE });
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          xTo((e.clientX - r.left - r.width / 2) * 0.12);
          yTo((e.clientY - r.top - r.height / 2) * 0.22);
        });
        btn.addEventListener('mouseleave', function () { xTo(0); yTo(0); });
      });
    }

    /* ==================================================================
       8. SMOOTHNESS — refresh trigger positions once fonts/images settle
       so nothing fires at a stale scroll position on a long page.
       ================================================================== */
    var refresh = function () { ScrollTrigger.refresh(); };
    window.addEventListener('load', refresh);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
    document.querySelectorAll(
      '.recovr-hero__media img, .recovr-page-hero__media img, .recovr-buybox__stage img'
    ).forEach(function (img) {
      if (img && !img.complete) img.addEventListener('load', refresh, { once: true });
    });
    [500, 1200, 2200].forEach(function (t) { setTimeout(refresh, t); });

    /* ==================================================================
       9. FAILSAFE — if GSAP half-dies (a trigger never fires, an element
       was scrolled past before hydration), never leave content hidden.
       Viewport-aware: only force-reveal things that are AT/ABOVE the fold
       (they "should" have animated) — genuinely below-fold elements stay
       in their pre-animation state so their scroll entrance is preserved.
       ================================================================== */
    var FAILSAFE_SELECTOR = [
      '[data-recovr-reveal]', '[data-recovr-done="1"]',
      '.recovr-values__card', '.recovr-reasons__card', '.recovr-subscribe-benefits__item',
      '.recovr-support-cards__card', '.recovr-why__card', '.recovr-leave-out__item',
      '.recovr-trust-bar__point', '.recovr-loved__tile', '.recovr-product-cards__item',
      '.cmp-row', '.recovr-stats__stat', '.recovr-review-rail .rv',
      '.recovr-hero__media', '.recovr-page-hero__media', '.recovr-founder-note__sig', '.recovr-contact-form__card',
      '.recovr-buybox__price', '.recovr-promo--banner', '.recovr-scarcity',
      '.recovr-buybox__form-buttons', '.recovr-plan-selector__label', '.recovr-buybox__stage',
    ].join(', ');
    setTimeout(function () {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      document.querySelectorAll(FAILSAFE_SELECTOR).forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top > vh * 0.95) return; // still below the fold → leave its entrance intact
        if (parseFloat(getComputedStyle(el).opacity) < 0.99) {
          gsap.set(el, { opacity: 1, y: 0, x: 0, scale: 1 });
          el.classList.add('in');
        }
      });
    }, 2500);

    // Re-measure after Shopify section reloads in the theme editor.
    document.addEventListener('shopify:section:load', refresh);

    // Expose a tiny hook for debugging / cleanup if ever needed.
    window.__recovrMotion = { refresh: refresh, kill: function () {
      ScrollTrigger.getAll().forEach(function (st) { st.kill(); });
      ctxTweens.forEach(function (t) { t.kill(); });
    } };
  }
})();
